import type { LocationGenerics } from "@/router/locationGenerics";

import LoadingError from "@/components/elements/routing/LoadingError";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import Center from "@/components/elements/layout/Center";
import { isRecoveredChunkPreloadError } from "@/helpers/chunk-preload-error";
import type { AsyncElement } from "@tanstack/react-location";
import type { ReactNode } from "react";

/**
 * A lazy route element with a failure path. react-location marks a match
 * `rejected` only from its `loader`, so a rejected `element` promise leaves the
 * match loading forever and `errorElement` never renders.
 */
export function lazyRouteElement(
  load: () => Promise<ReactNode>,
): AsyncElement<LocationGenerics> {
  return () =>
    load().catch((error: unknown) => {
      const message = error instanceof Error ? error.message : String(error);
      // The chunk is gone after a deploy and chunk-preload-error.ts has already
      // called reload(); the spinner covers the moment before the new page lands.
      if (isRecoveredChunkPreloadError(message)) {
        return (
          <Center className="h-full w-full">
            <LoadingSpinner />
          </Center>
        );
      }
      return <LoadingError error={error} />;
    });
}
